import 'leaflet-defaulticon-compatibility';

import Icon, { IconType } from '@atoms/Icon/Icon';
import { DEFAULT_MAP_CENTER, STORE_LOCATION } from '@constants/constants';
import useLocale from '@hooks/useLocale';
import Card, { ImageSize, ImageSizeBreakpoints } from '@molecules/Card/Card';
import PinIcon from '@svg/route-pin.svg';
import { MapLocation } from '@type-declarations/prepr';
import { formatAddress } from '@utils/formatAddress';
import getInternalRoute from '@utils/getInternalRoute';
import clsx from 'clsx';
import {
  divIcon,
  latLngBounds,
  LatLngExpression,
  Map,
  MapOptions,
  MarkerCluster,
} from 'leaflet';
import { GestureHandling } from 'leaflet-gesture-handling';
import { useEffect, useMemo, useRef } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import {
  MapContainer,
  Marker,
  Polyline,
  Popup,
  TileLayer,
  useMap,
} from 'react-leaflet';

import styles from './LeafletMap.module.scss';
import MarkerClusterGroup from './MarkerClusterGroup';

interface Props {
  route?: LatLngExpression[];
  currentLocation?: MapLocation;
  locations?: MapLocation[];
  disablePopup?: boolean;
  maxClusterRadius?: number;
  showStoreMarker?: boolean;
  flatCards?: boolean;
  markerImageSize?: ImageSize;
  markerImageSizeBreakpoints?: ImageSizeBreakpoints;
}

/* Gets static html for the custom Leaflet Marker */
const getMarkerIcon = (
  icon?: IconType,
  index?: number,
  isCurrentLocation?: boolean,
) =>
  divIcon({
    iconSize: [48, 60],
    iconAnchor: [24, 60],
    className: styles.marker,
    html: renderToStaticMarkup(
      <>
        <PinIcon
          className={clsx(
            styles.pin,
            isCurrentLocation && styles.currentLocationPin,
          )}
        />
        <Icon icon={icon || 'INFO_CIRCLE_SOLID'} className={styles.icon} />
        {typeof index === 'number' && (
          <span className={styles.index}>{index! + 1}</span>
        )}
      </>,
    ),
  });

const getStoreIcon = () =>
  divIcon({
    iconSize: [36, 36],
    iconAnchor: [18, 18],
    className: styles.storeMarker,
    html: renderToStaticMarkup(<div />),
  });

const getCluster = (cluster: MarkerCluster) =>
  divIcon({
    iconSize: [32, 32],
    iconAnchor: [16, 16],
    className: styles.cluster,
    html: renderToStaticMarkup(<span>{cluster.getChildCount()}</span>),
  });

function GestureHandlingSetter() {
  const map = useMap();

  useEffect(() => {
    // @ts-expect-error is imported with 'leaflet-gesture-handling' package
    map.gestureHandling.enable();
    map.addHandler('gestureHandling', GestureHandling);
  }, [map]);

  return null;
}

export default function LeafletMap({
  route = [],
  currentLocation,
  locations = [],
  disablePopup,
  maxClusterRadius = 80,
  showStoreMarker = false,
  flatCards = false,
  markerImageSize = 'sm',
  markerImageSizeBreakpoints = ['md@from-md', 'lg@from-lg'],
}: Props) {
  const locale = useLocale();

  const routeRef = useRef(null);
  const mapRef = useRef<Map>(null);

  const bounds = useMemo(() => {
    if (!currentLocation) {
      return [...route, ...locations.map(({ pos }) => pos)];
    }

    return [currentLocation.pos];
  }, [route, locations, currentLocation]);

  const filteredLocations = useMemo(() => {
    return [
      ...locations.filter(
        location =>
          location.pos[0] !== currentLocation?.pos[0] ||
          location.pos[1] !== currentLocation?.pos[1],
      ),
      currentLocation,
    ].filter(location => !!location);
  }, [locations, currentLocation]);

  useEffect(() => {
    if (!bounds.length) return;

    mapRef.current?.fitBounds(latLngBounds(bounds as LatLngExpression[]), {
      paddingTopLeft: [0, 40],
      paddingBottomRight: [0, 20],
    });
  }, [bounds]);

  let modifier: 'flat' | 'inStore';

  if (flatCards) {
    modifier = 'flat';
  } else if (showStoreMarker) {
    modifier = 'inStore';
  }

  const mapOptions: MapOptions = useMemo(() => {
    if (bounds.length === 0) {
      return {
        center: [DEFAULT_MAP_CENTER.lat, DEFAULT_MAP_CENTER.lon],
        zoom: 14, // default zoom when no locations/route is defined
      };
    }

    if (bounds.length === 1) {
      return {
        center: bounds[0] as LatLngExpression,
        zoom: 15,
      };
    }

    return {
      bounds: latLngBounds(bounds as LatLngExpression[]),
      boundsOptions: { paddingTopLeft: [0, 40], paddingBottomRight: [0, 10] },
    };
  }, [bounds]);

  return (
    <MapContainer
      ref={mapRef}
      className={styles.map}
      maxZoom={17}
      {...mapOptions}
    >
      {!showStoreMarker && <GestureHandlingSetter />}

      {/* This attribution layer is mandatory for copyright reasons */}
      <TileLayer
        attribution='&copy; <a href="https://www.stadiamaps.com/" target="_blank">Stadia Maps</a> &copy; <a href="https://openmaptiles.org/" target="_blank">OpenMapTiles</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}"
        /* Styling from old site: */
        // url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}"
        maxZoom={17}
      />

      {/* Route render */}
      {route && (
        <Polyline
          ref={routeRef}
          pathOptions={{ color: '#000', dashArray: '5,8', weight: 2 }}
          positions={route}
        />
      )}

      {showStoreMarker && (
        <Marker
          position={[STORE_LOCATION.lat, STORE_LOCATION.lon]}
          icon={getStoreIcon()}
        />
      )}

      {!!filteredLocations?.length && (
        <MarkerClusterGroup
          // @ts-expect-error iconCreateFunction does exist on MarkerClusterGroupOptions
          iconCreateFunction={getCluster}
          showCoverageOnHover={false}
          maxClusterRadius={maxClusterRadius} // 80 is default
        >
          {filteredLocations.map(location => (
            <Marker
              key={location.id}
              position={location.pos as LatLngExpression}
              icon={getMarkerIcon(
                location.icon,
                location.index,
                location.id === currentLocation?.id,
              )}
            >
              {!disablePopup && !location.disablePopup && (
                <Popup
                  offset={[0, -40]}
                  className={clsx(styles.popup, flatCards && styles.flatPopup)}
                  closeButton={false}
                >
                  {/* Wrapping div so the card does not completely re-render on store update */}
                  <div>
                    <Card
                      {...location}
                      key={location.id}
                      imageSize={markerImageSize}
                      imageSizeBreakpoints={markerImageSizeBreakpoints}
                      href={getInternalRoute({
                        internalLink: location,
                        locale,
                        store: showStoreMarker,
                      })}
                      showSaveButton
                      modifier={modifier}
                      subtitle={
                        location.address && formatAddress(location.address)
                      }
                      isPopup
                    />
                  </div>
                </Popup>
              )}
            </Marker>
          ))}
        </MarkerClusterGroup>
      )}
    </MapContainer>
  );
}
